import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate, formatTime } from '@/lib/utils';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { SkeletonCard } from '@/components/ui/Skeleton';

export function HomePage() {
  const today = new Date().toISOString().split('T')[0];

  const { data: teeTimes, isLoading: teeTimesLoading } = useQuery({
    queryKey: ['tee-times', 'featured'],
    queryFn: () => api.teeTimes.list({ dateFrom: today, pageSize: 6 }),
  });

  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ['products', 'featured'],
    queryFn: () => api.products.featured(),
  });

  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-sand-50 py-20 md:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-sage-100/50 via-transparent to-transparent" />
        <div className="container-wide relative">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold tracking-tight text-sand-900 sm:text-5xl md:text-6xl">
              Your perfect
              <br />
              <span className="text-sage-600">round awaits</span>
            </h1>
            <p className="mt-6 text-lg text-sand-600">
              Book tee times at premium courses, earn rewards with every round, and shop the latest gear from our pro shop.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/tee-times">
                <Button size="lg" variant="sage">
                  Book a Tee Time
                </Button>
              </Link>
              <Link to="/shop">
                <Button size="lg" variant="outline">
                  Browse Pro Shop
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section bg-white">
        <div className="container-wide">
          <div className="grid gap-8 sm:grid-cols-3">
            <div className="flex flex-col items-start">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage-100">
                <svg className="h-6 w-6 text-sage-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-sand-900">Easy Booking</h3>
              <p className="mt-2 text-sm text-sand-500">
                Browse available tee times and book in seconds. Real-time availability ensures you always get your preferred slot.
              </p>
            </div>
            <div className="flex flex-col items-start">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-terracotta-100">
                <svg className="h-6 w-6 text-terracotta-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-sand-900">Earn Rewards</h3>
              <p className="mt-2 text-sm text-sand-500">
                Earn points on every booking and purchase. Redeem for free rounds, cart rentals, and exclusive merchandise.
              </p>
            </div>
            <div className="flex flex-col items-start">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sand-100">
                <svg className="h-6 w-6 text-sand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-sand-900">Pro Shop</h3>
              <p className="mt-2 text-sm text-sand-500">
                Shop premium clubs, apparel, and accessories. Add items to your booking for pickup at the course.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Available Tee Times */}
      <section className="section bg-sand-50">
        <div className="container-wide">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-sand-900">Available Tee Times</h2>
              <p className="mt-2 text-sand-500">Book your next round today</p>
            </div>
            <Link to="/tee-times" className="text-sm font-medium text-sage-600 hover:text-sage-700">
              View all times
            </Link>
          </div>

          {teeTimesLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {teeTimes?.data?.slice(0, 6).map((teeTime) => (
                <Link key={teeTime.id} to={`/book/${teeTime.id}`}>
                  <Card variant="hover" className="h-full">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-sand-900">{teeTime.course?.name}</p>
                        <p className="text-sm text-sand-500">{teeTime.course?.city}, {teeTime.course?.state}</p>
                      </div>
                      {teeTime.isPeakTime && (
                        <Badge variant="terracotta">Peak</Badge>
                      )}
                    </div>
                    <div className="mt-4 flex items-center gap-4">
                      <div>
                        <p className="text-2xl font-bold text-sand-900">{formatTime(teeTime.time)}</p>
                        <p className="text-sm text-sand-500">{formatDate(teeTime.date, { month: 'short', day: 'numeric' })}</p>
                      </div>
                      <div className="ml-auto text-right">
                        <p className="text-lg font-semibold text-sage-600">{formatCurrency(teeTime.basePrice)}</p>
                        <p className="text-xs text-sand-400">{teeTime.availableSpots} spots left</p>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-sm text-sand-500">
                      <span>{teeTime.holes} holes</span>
                      <span className="text-sand-300">•</span>
                      <span>Cart: {formatCurrency(teeTime.cartPrice)}</span>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Products */}
      <section className="section bg-white">
        <div className="container-wide">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-sand-900">Pro Shop Picks</h2>
              <p className="mt-2 text-sand-500">Gear up for your next round</p>
            </div>
            <Link to="/shop" className="text-sm font-medium text-sage-600 hover:text-sage-700">
              Shop all
            </Link>
          </div>

          {productsLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {products?.data?.slice(0, 4).map((product) => (
                <Link key={product.id} to={`/shop/${product.id}`}>
                  <Card variant="hover" padding="none" className="overflow-hidden">
                    <div className="aspect-square bg-sand-100">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <svg className="h-12 w-12 text-sand-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="font-medium text-sand-900">{product.name}</p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="font-semibold text-sage-600">{formatCurrency(product.price)}</span>
                        {product.compareAtPrice && (
                          <span className="text-sm text-sand-400 line-through">
                            {formatCurrency(product.compareAtPrice)}
                          </span>
                        )}
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-sage-600">
        <div className="container-wide text-center">
          <h2 className="text-3xl font-bold text-white">Ready to hit the links?</h2>
          <p className="mt-4 text-lg text-sage-100">
            Join thousands of golfers who book their tee times with us.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-sage-700 hover:bg-sage-50">
                Create Free Account
              </Button>
            </Link>
            <Link to="/tee-times">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-sage-500">
                Browse Tee Times
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
